'use client';

import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';

import { SILVERTIGER_LOCALE_COOKIE } from '@/const/locale';
import { SILVERTIGER_THEME_APPEARANCE } from '@/const/theme';
import { defaultLocale, type Locale } from '@/i18n';
import { useGlobalStore } from '@/store/global';
import { LocaleMode } from '@/types_cp/locale';
import { getCookie, LOCALE_STORAGE_KEY, normalizeLocaleCandidate } from '@/utils/language-utils';

interface GlobalStoreProviderProps extends PropsWithChildren {
  initialLocale?: Locale | LocaleMode;
}

const GlobalStoreProvider = ({ children, initialLocale }: GlobalStoreProviderProps) => {
  const updateSystemStatus = useGlobalStore((s) => s.updateSystemStatus);

  useEffect(() => {
    const savedLocale =
      normalizeLocaleCandidate(getCookie(LOCALE_STORAGE_KEY)) ??
      normalizeLocaleCandidate(getCookie(SILVERTIGER_LOCALE_COOKIE));
    const localeToUse = (savedLocale ?? initialLocale ?? defaultLocale) as LocaleMode;

    const savedTheme = getCookie(SILVERTIGER_THEME_APPEARANCE);
    const themeToUse = (savedTheme as 'auto' | 'light' | 'dark') || 'auto';
    const currentLanguage = useGlobalStore.getState().status.language;

    updateSystemStatus({
      // Preserve a locale that may already have been applied by the embed bridge.
      ...(currentLanguage && currentLanguage !== 'auto' ? {} : { language: localeToUse }),
      themeMode: themeToUse,
    });
  }, [initialLocale, updateSystemStatus]);

  return children;
};

export default GlobalStoreProvider;
